"use client";

import { DownloadSimple, Eye, BookOpen } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useState } from "react";
import { BiblePlanModal } from "./bible-plan-modal";
import { DailyScriptureCard } from "./daily-scripture-card";
import type { Locale } from "@/i18n/config";

type DailyScriptureTranslations = {
  todaysScripture: string;
  nextReadingIn: string;
  hrs: string;
  min: string;
  sec: string;
  readingPlan: string;
  weeksFullBible: string;
  viewFullPlan: string;
  previewDisclaimer: string;
  planBeginsMessage: string;
  planCompletedMessage: string;
  restMessage: string;
};

type BiblePlanTranslations = {
  sectionLabel: string;
  title: string;
  titleHighlight: string;
  description: string;
  viewOnline: string;
  downloadPdf: string;
  weeks: string;
  fullBibleJourney: string;
  days: {
    sunday: string;
    monday: string;
    tuesday: string;
    wednesday: string;
    thursday: string;
    friday: string;
    saturday: string;
  };
  todaysReading: string;
  week: string;
  viewFullPlan: string;
  modalTitle: string;
  modalSubtitle: string;
  close: string;
  modalHint: string;
  dailyScripture?: DailyScriptureTranslations;
};

type BibleReadingPlanSectionProps = {
  locale?: Locale;
  translations?: BiblePlanTranslations;
};

export const BibleReadingPlanSection = ({ locale = 'en', translations }: BibleReadingPlanSectionProps) => {
  // Default translations for backwards compatibility
  const t: BiblePlanTranslations = translations || {
    sectionLabel: "Personal Growth",
    title: "Master the Word",
    titleHighlight: "in 2026",
    description: "Join our 53-week journey through the scriptures. Deepen your faith, understand your purpose, and stay on fire for God throughout the year.",
    viewOnline: "View Online",
    downloadPdf: "Download PDF",
    weeks: "53 Weeks",
    fullBibleJourney: "Full Bible Journey",
    days: {
      sunday: "Sunday",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
    },
    todaysReading: "Today's Reading",
    week: "Week",
    viewFullPlan: "View Full Plan",
    modalTitle: "2026 Bible Reading Plan",
    modalSubtitle: "53-Week Journey Through Scripture",
    close: "Close",
    modalHint: "Scroll inside the viewer to see all pages \u2022 Press ESC to close",
  };
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <section id="bible-plan" className="py-24 bg-white dark:bg-black overflow-hidden relative z-20 border-t border-zinc-100 dark:border-white/5 transition-colors duration-500">
      <BiblePlanModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        translations={{
          modalTitle: t.modalTitle,
          close: t.close,
          downloadPdf: t.downloadPdf,
          modalHint: t.modalHint,
        }}
      />
      {/* Background Stylized 2026 */}
      <div className="absolute inset-0 flex items-center justify-center opacity-[0.03] dark:opacity-5 pointer-events-none">
        <span className="text-[20rem] md:text-[45rem] font-black text-black dark:text-white select-none leading-none tracking-tighter">
          2026
        </span>
      </div>
      
      {/* Glow Effects */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-red/5 dark:bg-brand-red/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-20">
          <div className="max-w-2xl text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <span className="text-brand-red dark:text-brand-gold text-xs tracking-[0.5em] uppercase mb-8 block font-black">
                {t.sectionLabel}
              </span>
              <h2 className="font-serif text-6xl md:text-8xl font-bold text-black dark:text-white mb-10 leading-[0.9] tracking-tighter">
                {t.title} <br />
                <span className="italic text-brand-red">{t.titleHighlight}</span>
              </h2>
              <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-400 font-light leading-relaxed mb-16 max-w-xl mx-auto lg:mx-0">
                {t.description}
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-6 justify-center lg:justify-start">
                <button
                  onClick={() => setIsPreviewOpen(true)}
                  className="group flex items-center gap-4 bg-black dark:bg-white text-white dark:text-black px-10 py-6 rounded-2xl font-bold uppercase text-[10px] tracking-[0.3em] hover:bg-brand-red hover:text-white transition-all duration-500 shadow-xl active:scale-95"
                >
                  <Eye weight="bold" size={20} className="group-hover:scale-110 transition-transform" />
                  {t.viewOnline}
                </button>

                <a
                  href="/bible-plan-2026.pdf"
                  download
                  className="group flex items-center gap-4 border border-black/10 dark:border-white/10 text-black dark:text-white px-10 py-6 rounded-2xl font-bold uppercase text-[10px] tracking-[0.3em] hover:bg-black/5 dark:hover:bg-white/5 transition-all duration-500 active:scale-95"
                >
                  <DownloadSimple weight="bold" size={20} className="group-hover:translate-y-1 transition-transform" />
                  {t.downloadPdf}
                </a>
              </div>

              <div className="mt-12 flex items-center gap-5 text-zinc-400 dark:text-zinc-500 justify-center lg:justify-start">
                <div className="w-12 h-12 bg-black/5 dark:bg-white/5 rounded-xl border border-black/10 dark:border-white/10 flex items-center justify-center backdrop-blur-sm">
                  <BookOpen weight="fill" size={24} className="text-brand-red dark:text-brand-gold" />
                </div>
                <div className="flex flex-col items-start">
                  <span className="text-black dark:text-white font-bold tracking-tight text-sm">{t.weeks}</span>
                  <span className="text-[9px] uppercase tracking-widest font-black text-zinc-400 dark:text-zinc-600">{t.fullBibleJourney}</span>
                </div>
              </div>
            </motion.div>
          </div>

          <DailyScriptureCard
            onViewPlan={() => setIsPreviewOpen(true)}
            locale={locale}
            translations={translations?.dailyScripture}
          />
        </div>
      </div>
    </section>
  );
};